import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, MessageCircle, X } from "lucide-react";
import { waLink } from "../lib/constants";

export const ProjectLightbox = ({ projects, index, onClose, onChange }) => {
  const project = index >= 0 ? projects[index] : null;

  useEffect(() => {
    if (!project) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onChange((index + 1) % projects.length);
      if (e.key === "ArrowLeft") onChange((index - 1 + projects.length) % projects.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [project, index, projects.length, onClose, onChange]);

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          data-testid="project-lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 flex flex-col bg-slate-950 text-white"
          onClick={onClose}
        >
          <div className="flex h-16 shrink-0 items-center justify-between border-b border-white/10 px-5 sm:px-8">
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-400">
              {String(index + 1).padStart(2, "0")} / {String(projects.length).padStart(2, "0")}
            </span>
            <button
              data-testid="lightbox-close-button"
              onClick={onClose}
              aria-label="Fechar"
              className="flex h-10 w-10 items-center justify-center border border-white/15 text-slate-300 transition-colors hover:border-white hover:text-white"
            >
              <X size={18} />
            </button>
          </div>

          <div className="grid flex-1 min-h-0 lg:grid-cols-[1fr_380px]" onClick={(e) => e.stopPropagation()}>
            <div className="relative flex min-h-0 items-center justify-center bg-black/40 p-4 sm:p-8">
              <AnimatePresence mode="wait">
                <motion.img
                  key={project.id}
                  src={project.image_url}
                  alt={`Projeto ${index + 1}`}
                  data-testid="lightbox-image"
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.01 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="max-h-full max-w-full object-contain"
                />
              </AnimatePresence>
              <button
                data-testid="lightbox-prev-button"
                onClick={() => onChange((index - 1 + projects.length) % projects.length)}
                aria-label="Projeto anterior"
                className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-white/15 bg-slate-950/60 backdrop-blur transition-colors hover:border-white sm:left-6"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                data-testid="lightbox-next-button"
                onClick={() => onChange((index + 1) % projects.length)}
                aria-label="Próximo projeto"
                className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-white/15 bg-slate-950/60 backdrop-blur transition-colors hover:border-white sm:right-6"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            <aside className="overflow-y-auto border-t border-white/10 p-6 sm:p-8 lg:border-l lg:border-t-0">
              <p data-testid="lightbox-category" className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#C05621]">{project.category}</p>
              {project.description && (
                <p data-testid="lightbox-description" className="mt-6 text-sm leading-relaxed text-slate-300">{project.description}</p>
              )}
              <a
                data-testid="lightbox-whatsapp-button"
                href={waLink(`Olá! Vi o projeto ${String(index + 1).padStart(2, "0")} (${project.category}) no site da ZARI ENGENHARIA e gostaria de um projeto parecido.`)}
                target="_blank"
                rel="noreferrer"
                className="mt-8 flex items-center justify-center gap-3 border border-[#25D366] px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#25D366] transition-colors hover:bg-[#25D366] hover:text-white"
              >
                <MessageCircle size={15} /> Quero um projeto assim
              </a>
            </aside>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
